/**
 * Validation — issue validations and track usage, in one place.
 *
 * Composes three already-shipped screens into one tab, matching the demo
 * mockup's single "Validation" area, rather than splitting it across
 * Validate a car / My validations / Redemption history: a header + stat
 * tiles, a card per offer with an "Issue validation" quick action, the
 * existing usage chart + redemption log, and the existing code-management
 * section. None of the underlying screens changed — this only recomposes
 * how they're reached.
 *
 * The summary stays tenant-wide, while every commercial term stays on its
 * PROGRAM card. A tenant can hold included, chargeback, and prepaid programs
 * at once, so a mixed summary says "Varies" and the cards name each model and
 * its bound. Usage inventory remains a separate count with its own reset.
 * The issued total comes from `getTenantBilling()` rather than the capped
 * redemption-history list, so a busy tenant is not silently under-counted.
 *
 * The commercial bound's CONSUMPTION (TEN-09) comes from the server on each
 * program (`commercial`): the spend limit's month and the prepaid block's
 * issued grants, with the gate's own verdict. Figures arrive only for readers
 * holding `validations.reporting`; the verdict and warning tier arrive for
 * everyone who can issue. Asking for more (TEN-08) names the program it is
 * about; the unscoped ask at the foot of the page still names none.
 */
import { useEffect, useRef, useState, type CSSProperties } from 'react'
import type { TenantSession } from '../lib/session'
import {
  listValidations, listValidationRequests, getTenantBilling, estimateValidation,
  tenantCan, requestMoreValidations, requestProgramKey,
  type Validation as ValidationOffer, type ValidationRequest, type TenantAccrual, type MeResponse, type ValidationEstimate,
} from '../lib/api'
import { Validate, validationDisplayLabel } from './Validate'
import { History } from './History'
import { Dashboard } from './Dashboard'
import { Stations } from './Stations'
import { labelStyle as baseLabel, buttonRadius } from '../ui'
import { formatValidationHours, lotClockLabel, lotZoneAbbrev } from '../lib/validationHours'
import { issuableOffers } from '../lib/issuableOffers'
import { inventoryState, isOfferUsedUp, usedUpLabel } from '../lib/offerEligibility'
import { generateBrandedQrSvg } from '../lib/qrBrand'
import { offerFundingLine } from '../lib/accountState'

const primaryBtnStyle: CSSProperties = {
  padding: '10px 16px', borderRadius: 9, background: '#7F3F98', color: '#fff', border: 'none',
  fontWeight: 800, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
}
const ghostBtnStyle: CSSProperties = {
  ...primaryBtnStyle, background: '#fff', color: 'var(--color-gray-700)', border: '1.5px solid var(--color-gray-300)',
}

/* Same treatment as every other screen's label; this one keeps its own
   bottom margin (and Register its `display`), which is spacing, not type. */
const labelStyle: CSSProperties = { ...baseLabel,
  marginBottom: 8, }

const issueOverlay: CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(26,26,26,.44)',
  display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '48px 20px', overflowY: 'auto',
}

const issueModal: CSSProperties = {
  width: 'min(460px, 100%)', background: '#fff', borderRadius: 16,
  boxShadow: '0 30px 70px rgba(0,0,0,.30)', overflow: 'hidden',
}

const money = (cents: number) =>
  `$${(Math.max(0, cents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

/**
 * Can staff issue this offer right now? The SERVER answers (`availableNow`,
 * computed in the lot's own clock by the list route) and this only reads it.
 *
 * ⚠️ Never derive it from `Date#getDay()`/`getHours()`: those read the VIEWER's
 * timezone, so a manager in Chicago looking at a Pacific lot saw the window two
 * hours off and could be refused an offer that was open (demo #89). Absent
 * `availableNow` on a windowed offer means an API older than the field; fail
 * open there, because redemption re-checks the window in the lot's clock
 * (`evaluateValidation`) and refuses anything outside it.
 */
export function offerAvailableNow(offer: ValidationOffer): boolean {
  if (!offer.activeHours) return true
  return offer.availableNow ?? true
}

/**
 * The billing line reads e.g. "412 validations redeemed" — pull the leading
 * number out for the tile's headline, but fall back to the whole label if a
 * future format ever stops starting with one. Never recompute the count
 * from raw redemption rows instead: that endpoint caps at the 100 most
 * recent (see History.tsx), which would silently undercount a busier tenant
 * against this screen's own accurate total.
 */
function issuedTile(a: TenantAccrual | null, known: boolean): { value: string; caption: string } {
  // ⚠️ "NOT LOADED" AND "ZERO" ARE DIFFERENT ANSWERS, and this tile used to give
  // the same one to both. `known` is false only when the billing read FAILED
  // (it is swallowed to null at the call site); an accrual that loaded with no
  // `validation_discounts` line means the tenant genuinely issued nothing this
  // month, which is 0 — and is exactly the state a tenant going live is in on
  // day one. Showing them "—" next to a confident "$0.00" read as broken.
  if (!known) return { value: '—', caption: 'issued this month' }
  const label = a?.lines.find(l => l.kind === 'validation_discounts')?.label
  if (!label) return { value: '0', caption: 'issued this month' }
  const m = label.match(/^([\d,]+)\s+(.*)/)
  return m ? { value: m[1], caption: m[2] } : { value: label, caption: 'this month' }
}

/**
 * "Worth $X now, up to $Y at the evening rate" — a live rate-based estimate
 * for the three time-based discount types. `null` when both figures are
 * unknown (a flat/full/percent offer, or a zone this can't be metered
 * against right now) — say nothing rather than render a blank dash, matching
 * this file's own "no invented figures" rule for the stat tiles above.
 */
function estimateLine(e: ValidationEstimate | undefined): string | null {
  if (!e || (e.nowCents == null && e.eveningCents == null)) return null
  if (e.nowCents == null) return `Worth ${money(e.eveningCents!)} at the evening rate`
  if (e.eveningCents == null) return `Worth ${money(e.nowCents)} right now`
  if (e.eveningCents === e.nowCents) return `Worth ${money(e.nowCents)} right now`
  return e.eveningCents > e.nowCents
    ? `Worth ${money(e.nowCents)} now, up to ${money(e.eveningCents)} at the evening rate`
    : `Worth ${money(e.nowCents)} now, ${money(e.eveningCents)} at the evening rate`
}

/**
 * Mirrors the server's `INVENTORY_LOW_REMAINING_SHARE` (validations/index.ts).
 * Deliberately the same number so the banner below appears on exactly the
 * redemption that triggers the warning SMS — a tenant reading "running low" on
 * screen and getting the text a day later would read as two different systems
 * disagreeing about their own account.
 */
const LOW_REMAINING_SHARE = 0.2

/** Uses left on a capped offer, or null when uncapped OR unknown (never 0). */
function remainingOf(o: ValidationOffer): number | null {
  return inventoryState(o) === 'available' || inventoryState(o) === 'exhausted' ? o.remaining! : null
}

/**
 * Can staff issue this offer right now? Its access window is open AND no
 * reported bound is spent: not the inventory, not the spend limit, not the
 * prepaid block (lib/offerEligibility.ts). The picker, the cards and the
 * Validate form all answer this the same way; unknown stays issuable.
 */
export function offerIssuable(offer: ValidationOffer): boolean {
  return offerAvailableNow(offer) && !isOfferUsedUp(offer)
}

/**
 * Can this program run out at all, so asking for more means something? A
 * usage inventory, or a commercial bound (a monthly spend limit, a prepaid
 * block). An included or uncapped program has nothing to raise, so it offers
 * no ask of its own (the unscoped ask still exists).
 *
 * The server's `commercial.bound` decides when it is there. A reader it was
 * withheld from (a zone-pinned reader of a multi-zone program, an older API)
 * falls back to the configured terms, which every tenant reader receives. This
 * only decides whether a button renders; it moves nothing.
 */
export function canRunOut(o: ValidationOffer): boolean {
  if (o.inventory) return true
  if (o.commercial) return o.commercial.bound !== 'none'
  return (o.funding === 'chargeback' && o.spendLimit?.kind === 'amount')
    || (o.funding === 'prepaid' && o.prepaidLimit != null)
}

/** The key the unscoped ask is tracked under; a program's ask uses `requestProgramKey`. */
export const UNSCOPED_ASK = '__tenant'

/**
 * Which asks are still open, by program key, so a "Requested" receipt survives
 * a reload. Pending and acknowledged both mean the operator has not answered;
 * a fulfilled or declined ask frees the button for a new one. Each program's
 * receipt is independent of the others and of the unscoped ask.
 */
export function openAskKeys(requests: ValidationRequest[]): string[] {
  return [...new Set(requests
    .filter(r => r.status === 'pending' || r.status === 'acknowledged')
    .map(r => r.programId ?? UNSCOPED_ASK))]
}

export type CommercialNotice = { tone: 'info' | 'warn' | 'stop'; headline: string; detail: string }

/**
 * The per-program notice for the commercial bound, or null. Figures render
 * only when the server sent them (reporting readers) and, for dollars, only on
 * a tenant whose lots show money. A Validation-only user gets the same notice
 * at the same moment, in words with no number: the warning is the point of the
 * level, the spend is not theirs to see.
 */
export function commercialNotice(o: ValidationOffer, opts: { hideMoney: boolean }): CommercialNotice | null {
  const c = o.commercial
  if (!c || c.bound === 'none') return null
  const name = o.name || o.code
  const guests = opts.hideMoney ? 'It cannot be issued' : 'Guests pay their own parking'
  if (c.status === 'reached') {
    return c.bound === 'prepaid_block'
      ? { tone: 'stop', headline: `${name}'s prepaid block is used up`, detail: `${guests} until your Parkify contact adds to the block.` }
      : { tone: 'stop', headline: `${name} has reached its monthly limit`, detail: `${guests} until the limit resets next month or your Parkify contact raises it.` }
  }
  if (c.status !== 'open' || !c.warning) return null
  if (c.bound === 'prepaid_block') {
    const left = c.block?.remaining
    return {
      tone: 'warn',
      headline: typeof left === 'number'
        ? `${left} ${left === 1 ? 'validation' : 'validations'} left in ${name}'s prepaid block`
        : `${name}'s prepaid block is running low`,
      detail: 'Issuing stops when the block is used up. Your Parkify contact can add to it.',
    }
  }
  const s = c.spend
  const figures = s && !opts.hideMoney ? `${money(s.spentCents)} of its ${money(s.limitCents)} monthly limit` : null
  return {
    tone: c.warning === 'urgent' ? 'warn' : 'info',
    headline: figures
      ? `${name} has used ${figures}`
      : c.warning === 'urgent' ? `${name} is nearly at its monthly limit` : `${name} is getting close to its monthly limit`,
    detail: 'Issuing stops when the limit is reached. Your Parkify contact can raise it.',
  }
}

/** The card's commercial figure line, for reporting readers only (the server sends nothing else). */
export function commercialFigureLine(o: ValidationOffer, opts: { hideMoney: boolean }): string | null {
  const c = o.commercial
  if (!c) return null
  if (c.status === 'unknown' && c.bound !== 'none') return 'Limit status unavailable right now'
  if (c.spend && !opts.hideMoney) return `${money(c.spend.spentCents)} of ${money(c.spend.limitCents)} used this month`
  if (c.block) {
    const parts = [`${c.block.used.toLocaleString('en-US')} of ${c.block.size.toLocaleString('en-US')} in the block used`]
    if (c.block.awaitingRedemption) parts.push(`${c.block.awaitingRedemption} awaiting redemption`)
    if (c.block.returned) parts.push(`${c.block.returned} returned unused`)
    return parts.join(' · ')
  }
  return null
}

const NOTICE_TONE: Record<CommercialNotice['tone'], { bg: string; border: string; fg: string }> = {
  info: { bg: 'var(--color-gray-100)', border: 'var(--color-gray-200)', fg: 'var(--color-gray-800)' },
  warn: { bg: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.35)', fg: '#92400e' },
  stop: { bg: 'rgba(237,28,36,0.08)', border: 'rgba(237,28,36,0.35)', fg: '#b91c1c' },
}

type AskState = 'idle' | 'sending' | 'sent'

/**
 * The per-program ask inside a warning. Once sent it becomes the receipt,
 * saying what happens next, rather than a spent button.
 */
function AskAction({ state, error, onAsk, hideMoney }: { state: AskState; error?: string; onAsk: () => void; hideMoney: boolean }) {
  if (state === 'sent') {
    return (
      <div style={{ marginTop: 10, background: '#fff', border: '1px solid var(--color-gray-200)', borderRadius: 8, padding: '8px 11px' }}>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--color-gray-700)' }}>Requested</div>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)', marginTop: 2, lineHeight: 1.45 }}>
          {hideMoney ? 'Your Parkify contact will review it.' : 'Your Parkify contact will review it. Nothing is charged until they agree.'}
        </div>
      </div>
    )
  }
  return (
    <div style={{ marginTop: 10 }}>
      <button type="button" onClick={onAsk} disabled={state === 'sending'} style={{ ...ghostBtnStyle, padding: '7px 12px', fontSize: 12, opacity: state === 'sending' ? .65 : 1 }}>
        {state === 'sending' ? 'Sending…' : 'Request more validations'}
      </button>
      {error && <div style={{ color: '#b91c1c', fontSize: 12, fontWeight: 600, marginTop: 6 }}>{error}</div>}
    </div>
  )
}

/** Same condition the server alerts on: used has reached the 80% mark. */
function isRunningLow(o: ValidationOffer): boolean {
  const left = remainingOf(o)
  if (left == null || !o.inventory) return false
  const lowAt = Math.ceil(o.inventory.cap * (1 - LOW_REMAINING_SHARE))
  return o.inventory.cap - left >= lowAt
}

const periodWord = (reset: string) => (reset === 'rolling_30d' ? 'in the last 30 days' : 'this month')

function fundingSummary(offers: ValidationOffer[]): { value: string; caption: string } | null {
  if (offers.length === 0) return null
  const values = new Set(offers.map(o => o.funding))
  if (values.size !== 1) return { value: 'Varies', caption: 'validation funding by offer' }
  const funding = offers[0]?.funding
  if (funding === 'included') return { value: 'Included', caption: 'validation cost to you' }
  if (funding === 'chargeback') return { value: 'Cost to you', caption: 'validation funding' }
  if (funding === 'prepaid') return { value: 'Prepaid', caption: 'validation funding' }
  return { value: 'Not set', caption: 'validation funding' }
}

function inventoryResetLabel(reset: NonNullable<ValidationOffer['inventory']>['reset']): string {
  return reset === 'rolling_30d' ? 'every 30 days' : 'each calendar month'
}

/**
 * Which QR this offer's card should show.
 *
 *  'custom'    — the business's OWN uploaded artwork, which REPLACES the
 *                generated code. This card is the tenant looking at their own
 *                signage ("the QR code your guests scan"), so showing them a
 *                generated code they never printed is the wrong answer; ops
 *                already shows the uploaded image on its side, and the two
 *                disagreeing is what made this a bug report.
 *  'generated' — no upload, so the branded code built from `arrivalUrl`.
 *  'none'      — no upload and no zone, so no link that pre-applies anything.
 *
 * Exported for test: this package has no rendering harness, so the decision
 * lives here as a pure function rather than being trapped in the component.
 */
export type OfferQrSource =
  | { kind: 'custom'; src: string }
  | { kind: 'generated'; url: string }
  | { kind: 'none' }

export function offerQrSource(offer: Pick<ValidationOffer, 'customQrUrl' | 'arrivalUrl'>): OfferQrSource {
  // Upload wins even with no zone: it is the artwork they actually printed, and
  // it stays worth showing when there is nothing to copy alongside it.
  if (offer.customQrUrl) return { kind: 'custom', src: offer.customQrUrl }
  if (offer.arrivalUrl) return { kind: 'generated', url: offer.arrivalUrl }
  return { kind: 'none' }
}

/** File extension for a `data:image/<type>;…` URI; `png` when it can't be read. */
export function dataUriExtension(uri: string): string {
  const mime = /^data:image\/([a-z0-9.+-]+)[;,]/i.exec(uri)?.[1]?.toLowerCase()
  if (!mime) return 'png'
  return mime === 'svg+xml' ? 'svg' : mime === 'jpeg' ? 'jpg' : mime
}

function OfferQrActions({ offer, copied, onCopied }: {
  offer: ValidationOffer
  copied: boolean
  onCopied: () => void
}) {
  const [qrSvg, setQrSvg] = useState('')
  const source = offerQrSource(offer)
  const custom = source.kind === 'custom' ? source.src : undefined

  useEffect(() => {
    let current = true
    setQrSvg('')
    // Don't spend a render generating a code that the custom image will cover.
    if (custom || !offer.arrivalUrl) return () => { current = false }
    void generateBrandedQrSvg(offer.arrivalUrl)
      .then(svg => { if (current) setQrSvg(svg) })
      .catch(() => { /* The copyable URL remains the source of truth. */ })
    return () => { current = false }
  }, [offer.arrivalUrl, custom])

  const copyLink = async () => {
    if (!offer.arrivalUrl) return
    try {
      await navigator.clipboard.writeText(offer.arrivalUrl)
      onCopied()
    } catch {
      // Clipboard permission is optional; the URL remains available through the
      // browser's link context menu and the QR download.
    }
  }

  const downloadQr = () => {
    const a = document.createElement('a')
    if (custom) {
      // A `data:` URI downloads directly — no Blob round-trip, and no object
      // URL to revoke. Extension comes from the URI's own mime so the saved
      // file opens in the right app.
      a.href = custom
      a.download = `parkify-validation-${offer.code}.${dataUriExtension(custom)}`
      a.click()
      return
    }
    if (!qrSvg) return
    const url = URL.createObjectURL(new Blob([qrSvg], { type: 'image/svg+xml' }))
    a.href = url
    a.download = `parkify-validation-${offer.code}.svg`
    a.click()
    URL.revokeObjectURL(url)
  }

  if (source.kind === 'none') {
    return (
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)' }}>
        Applies to all zones — issue it by plate or phone.
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      {custom ? (
        <img
          src={custom}
          alt="Your uploaded validation QR"
          style={{ width: 48, height: 48, flex: '0 0 auto', background: '#fff', border: '1px solid var(--color-gray-200)', borderRadius: 8, objectFit: 'contain' }}
        />
      ) : (
        <div
          aria-label="Validation QR preview"
          style={{ width: 48, height: 48, flex: '0 0 auto', background: '#fff', border: '1px solid var(--color-gray-200)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}
          dangerouslySetInnerHTML={{ __html: qrSvg || '<span style="font-size:10px;color:#777">QR</span>' }}
        />
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {offer.arrivalUrl && (
          <button type="button" onClick={() => void copyLink()} style={{ ...ghostBtnStyle, padding: '8px 12px', fontSize: 12 }}>
            {copied ? 'Copied' : 'Copy link'}
          </button>
        )}
        <button type="button" onClick={downloadQr} disabled={!custom && !qrSvg} style={{ ...ghostBtnStyle, padding: '8px 12px', fontSize: 12, opacity: (custom || qrSvg) ? 1 : .6 }}>
          Download QR
        </button>
      </div>
      {custom && (
        <div style={{ flexBasis: '100%', fontSize: 11, color: 'var(--color-gray-600)' }}>
          Your own uploaded artwork — this is what guests see, not a Parkify-generated code.
        </div>
      )}
    </div>
  )
}

function StatTile({ value, caption }: { value: string; caption: string }) {
  return (
    <div style={{ background: '#fff', border: '1px solid var(--color-gray-200)', borderRadius: 12, padding: '14px 18px', flex: '1 1 180px', minWidth: 160 }}>
      <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.6px' }}>{value}</div>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)', marginTop: 2 }}>{caption}</div>
    </div>
  )
}

export function Validation({ session, me, validationOnly = false, autoIssue, onAutoIssueHandled }: { session: TenantSession; me: MeResponse; validationOnly?: boolean; autoIssue?: boolean; onAutoIssueHandled?: () => void }) {
  const canReport = tenantCan(me, 'validations.reporting')
  const canManage = tenantCan(me, 'validations.request')
  const [offers, setOffers] = useState<ValidationOffer[]>([])
  const [billing, setBilling] = useState<TenantAccrual | null>(null)
  /**
   * Did the billing read ANSWER? Distinct from `billing === null`, which the
   * catch below also produces on failure — and the two must render differently:
   * a zero we measured is a fact, a zero we invented is a lie about money.
   * Billing.tsx states the same rule for the same figure ("a failed read must
   * never render as $0.00"); this screen shows that figure too.
   */
  const [billingKnown, setBillingKnown] = useState(false)
  const [estimates, setEstimates] = useState<Record<string, ValidationEstimate>>({})
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [activeCode, setActiveCode] = useState('')
  /**
   * Asks for more, per program key and one for the unscoped ask (TEN-08).
   * Independent on purpose: asking about one program must not grey out the
   * button on another, and the unscoped ask is its own thing.
   */
  const [asks, setAsks] = useState<Record<string, AskState>>({})
  const [askErrors, setAskErrors] = useState<Record<string, string>>({})
  // Collapsed by default — matches the demo's clean idle view (offer cards
  // + chart, nothing else). The lookup form only appears after the manager
  // chooses an available validation in the issue flow.
  const [lookupOpen, setLookupOpen] = useState(false)
  const [issueModalOpen, setIssueModalOpen] = useState(false)
  const [issueOffer, setIssueOffer] = useState<ValidationOffer | null>(null)
  // Availability is evaluated at the moment the modal opens. Keep that instant
  // stable while the modal is open so a minute boundary cannot change the cards
  // underneath a staff member's finger.
  const [issueNow, setIssueNow] = useState(() => new Date())
  const [copiedCode, setCopiedCode] = useState('')
  const lookupRef = useRef<HTMLDivElement>(null)
  // The server's `availableNow` is true as of the list read, which may be
  // hours old by the time the modal opens. Re-read it on every open and keep
  // it apart from `offers`, so a refresh does not re-run the estimate calls.
  const [liveWindow, setLiveWindow] = useState<Record<string, Pick<ValidationOffer, 'availableNow' | 'timezone' | 'remaining' | 'commercial'>>>({})
  const openIssue = (offer: ValidationOffer | null) => {
    setIssueOffer(offer)
    setIssueNow(new Date())
    setIssueModalOpen(true)
    if (session.kind === 'device') return
    listValidations(session.token)
      .then(all => setLiveWindow(Object.fromEntries(all.map(v => [v.code, { availableNow: v.availableNow, timezone: v.timezone, remaining: v.remaining, commercial: v.commercial }]))))
      .catch(() => { /* keep the list-time answer; redemption re-checks */ })
  }

  useEffect(() => {
    if (autoIssue) {
      openIssue(null)
      onAutoIssueHandled?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoIssue, onAutoIssueHandled])

  useEffect(() => {
    if (lookupOpen) lookupRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [lookupOpen, activeCode])

  useEffect(() => {
    if (session.kind === 'device') return   // a tablet 403s here; App.tsx never routes one
    setLoading(true); setLoadError('')
    void (async () => {
      try {
        const [all, bill] = await Promise.all([
          listValidations(session.token),
          canReport ? getTenantBilling(session.token).catch(() => null) : Promise.resolve(null),
        ])
        const ev = issuableOffers(all)
        setOffers(ev)
        // `bill === null` ⇒ the call threw (see the .catch above). The route
        // itself 503s rather than returning a null accrual, so a non-null
        // response always carries a real `current`.
        setBilling(bill?.current ?? null)
        setBillingKnown(bill !== null)
        setActiveCode(prev => prev || ev[0]?.code || '')
      } catch (e) {
        // An unavailable list is not an empty configuration. Keep the
        // no-program state reserved for a successful, empty response.
        setLoadError(e instanceof Error ? e.message : 'Could not load validations')
      } finally { setLoading(false) }
    })()
  }, [session, canReport])

  // Open asks, so each program's "Requested" receipt survives a reload. A
  // failed read only loses the receipts; asking still works.
  useEffect(() => {
    if (session.kind === 'device' || !canManage) return
    let current = true
    listValidationRequests(session.token)
      .then(({ requests }) => {
        if (!current) return
        const open = Object.fromEntries(openAskKeys(requests).map(k => [k, 'sent' as const]))
        setAsks(prev => ({ ...open, ...prev }))
      })
      .catch(() => { /* receipts are a convenience */ })
    return () => { current = false }
  }, [session, canManage])

  // A second, independent effect: the estimate is a nice-to-have preview, not
  // part of the offer card's own data, so a slow or failed estimate call must
  // never hold up (or blank) the discount label itself. Keyed on `offers`
  // since it can only run once they've resolved.
  useEffect(() => {
    if (offers.length === 0 || validationOnly) { setEstimates({}); return }
    void Promise.all(
      offers.map(o => estimateValidation(session.token, o.code).then(e => [o.code, e] as const).catch(() => null)),
    ).then(rows => setEstimates(Object.fromEntries(rows.filter((r): r is [string, ValidationEstimate] => r != null))))
  }, [offers, session, validationOnly])

  if (session.kind === 'device') return null

  const issued = issuedTile(billing, billingKnown)
  const live = (o: ValidationOffer): ValidationOffer => liveWindow[o.code] ? { ...o, ...liveWindow[o.code] } : o
  const issueList = offers.map(live)
  const issueOffers = issueList.filter(o => offerIssuable(o))
  const closedOffers = issueList.filter(o => !offerAvailableNow(o))
  const selectedIssueOffer = issueOffer && offerIssuable(live(issueOffer)) ? issueOffer : null
  const closedClock = lotClockLabel(issueNow, closedOffers.find(o => o.timezone)?.timezone)
  const funding = validationOnly ? null : fundingSummary(offers)
  /** One sender for every entry point, so the card's ask and the foot's ask cannot differ in what they send. */
  const sendRequest = async (program: ValidationOffer | null) => {
    const key = program ? requestProgramKey(program) : UNSCOPED_ASK
    if (asks[key] === 'sending' || asks[key] === 'sent') return
    setAsks(a => ({ ...a, [key]: 'sending' })); setAskErrors(e => ({ ...e, [key]: '' }))
    try {
      await requestMoreValidations(session.token, program ? { programId: key } : {})
      setAsks(a => ({ ...a, [key]: 'sent' }))
    } catch (e) {
      setAsks(a => ({ ...a, [key]: 'idle' }))
      setAskErrors(er => ({ ...er, [key]: e instanceof Error ? e.message : 'Could not send request' }))
    }
  }
  const askOf = (program: ValidationOffer | null): AskState => asks[program ? requestProgramKey(program) : UNSCOPED_ASK] ?? 'idle'
  const askErrorOf = (program: ValidationOffer | null): string | undefined => askErrors[program ? requestProgramKey(program) : UNSCOPED_ASK] || undefined
  /** The ask inside a program's warning: only where there is a bound to raise and the permission to ask. */
  const programAsk = (o: ValidationOffer) => canManage && canRunOut(o)
    ? <AskAction state={askOf(o)} error={askErrorOf(o)} onAsk={() => void sendRequest(o)} hideMoney={validationOnly} />
    : null
  const unscopedAsk = askOf(null)

  return (
    <div>
      {canReport && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <StatTile value={loading ? '—' : issued.value} caption={issued.caption} />
        {/* `—`, not $0.00, when the read failed. `?? 0` here asserted "no
            parking was covered" on a call that never answered — the one wrong
            answer nobody double-checks, on a money figure. */}
          {funding && <StatTile value={funding.value} caption={funding.caption} />}
        </div>
      )}

      {!loading && loadError && (
        <div style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.35)', borderRadius: 12, padding: '14px 16px', marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#92400e' }}>Validation offers aren’t available right now</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)', marginTop: 3, lineHeight: 1.5 }}>
            We couldn’t read this tenant’s validation programs. Nothing here confirms that no offers are configured; try again in a moment.
          </div>
        </div>
      )}

      {/* A Validation-only user cannot set a program up, so the demo's empty
          state for them is the one line "No validation program yet." with no
          setup guidance and no ask (Austin, #product-dev, 2026-10-07). */}
      {!loading && !loadError && offers.length === 0 && me.access?.level === 'validation' && (
        <div style={{ background: 'var(--color-gray-100)', border: '1px solid var(--color-gray-200)', borderRadius: 12, padding: '16px', marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--color-gray-800)' }}>No validation program yet.</div>
        </div>
      )}

      {!loading && !loadError && offers.length === 0 && me.access?.level !== 'validation' && (
        <div style={{ background: 'var(--color-gray-100)', border: '1px solid var(--color-gray-200)', borderRadius: 12, padding: '16px', marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--color-gray-800)' }}>No validations yet</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)', marginTop: 3, lineHeight: 1.5 }}>
            {canManage
              ? 'Your Parkify contact sets up validation programs. Once one is added, every active validation can be issued from this screen, whichever delivery method it uses.'
              : 'Your account can issue validations once your Parkify contact sets one up.'}
            </div>
          {canManage && (
            <div style={{ marginTop: 12 }}>
              <button onClick={() => void sendRequest(null)} disabled={unscopedAsk !== 'idle'} style={{ ...primaryBtnStyle, borderRadius: buttonRadius, padding: '9px 15px', background: unscopedAsk === 'sent' ? 'var(--color-gray-200)' : '#7F3F98', color: unscopedAsk === 'sent' ? 'var(--color-gray-700)' : '#fff', fontSize: 12, cursor: unscopedAsk === 'idle' ? 'pointer' : 'default' }}>
                {unscopedAsk === 'sent' ? 'Request sent' : unscopedAsk === 'sending' ? 'Sending…' : 'Request more validations'}
              </button>
              {askErrorOf(null) && <div style={{ color: '#b91c1c', fontSize: 12, fontWeight: 600, marginTop: 6 }}>{askErrorOf(null)}</div>}
            </div>
          )}
        </div>
      )}

      {!loading && !loadError && offers.length > 0 && (
        <div style={{ background: 'var(--color-gray-100)', border: '1px solid var(--color-gray-200)', borderRadius: 12, padding: '12px 14px', marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--color-gray-700)', marginBottom: 3 }}>Validation planning context</div>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)', lineHeight: 1.5 }}>
            {offers.length} active {offers.length === 1 ? 'offer is' : 'offers are'} available. Allowances are tracked per offer and reset on each offer’s own schedule; the cards below are the source of truth for what remains.
            {!canReport && ' Usage totals are restricted for this account, so only issuance and offer controls are shown.'}
          </div>
        </div>
      )}

      {/* An allowance running down is the one thing on this screen with a
          deadline, so it leads — but only once it matters, and it names the
          offer rather than aggregating. Summing caps across offers would be a
          fabricated total: a rolling-30-day cap and a calendar-month cap don't
          add up to a single "this month" number. */}
      {offers.filter(isRunningLow).map(o => {
        const left = remainingOf(o) ?? 0
        const spent = left <= 0
        return (
          <div
            key={`low-${o.code}`}
            style={{
              background: spent ? 'rgba(237,28,36,0.08)' : 'rgba(245,158,11,0.10)',
              border: `1px solid ${spent ? 'rgba(237,28,36,0.35)' : 'rgba(245,158,11,0.35)'}`,
              borderRadius: 12, padding: '14px 16px', marginBottom: 12,
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 800, color: spent ? '#b91c1c' : '#92400e' }}>
              {spent
                ? `${o.name || o.code} has used its full allowance ${periodWord(o.inventory!.reset)}`
                : `${left} ${left === 1 ? 'validation' : 'validations'} left ${periodWord(o.inventory!.reset)} on ${o.name || o.code}`}
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)', marginTop: 3, lineHeight: 1.5 }}>
              {spent
                ? validationOnly
                  ? 'This validation cannot be issued until it resets. Contact your Parkify rep to add more.'
                  : 'Guests pay their own parking until it resets. Contact your Parkify rep to add more.'
                : validationOnly
                  ? `This validation cannot be issued once it is used up. We'll text you when it runs out.`
                  : `Guests pay their own parking once it's used up. We'll text you when it runs out.`}
            </div>
            {/* One ask per program: when its commercial notice below also
                shows, the ask lives there. */}
            {!commercialNotice(o, { hideMoney: validationOnly }) && programAsk(o)}
          </div>
        )
      })}

      {/* The commercial bound, per program (TEN-09): a monthly spend limit at
          75%, 90% and reached, a prepaid block running low and used up. Never
          rolled up: a program at 90% beside one at 10% is two facts. Every
          level sees these; only reporting readers see figures in them. */}
      {offers.flatMap(o => {
        const notice = commercialNotice(o, { hideMoney: validationOnly })
        if (!notice) return []
        const tone = NOTICE_TONE[notice.tone]
        return [(
          <div
            key={`commercial-${o.code}`}
            role={notice.tone === 'stop' ? 'alert' : undefined}
            style={{ background: tone.bg, border: `1px solid ${tone.border}`, borderRadius: 12, padding: '14px 16px', marginBottom: 12 }}
          >
            <div style={{ fontSize: 14, fontWeight: 800, color: tone.fg }}>{notice.headline}</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)', marginTop: 3, lineHeight: 1.5 }}>{notice.detail}</div>
            {programAsk(o)}
          </div>
        )]
      })}

      {offers.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={labelStyle}>Offers</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {offers.map(o => {
              const estimate = validationOnly ? null : estimateLine(estimates[o.code])
              const exhausted = isOfferUsedUp(o)
              const fundingLine = validationOnly ? null : offerFundingLine(o)
              const commercialLine = commercialFigureLine(o, { hideMoney: validationOnly })
              return (
              <div key={o.code} style={{ background: '#fff', border: '1px solid var(--color-gray-200)', borderRadius: 10, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 800 }}>{offers.length === 1 ? 'Your validation code' : o.name || o.code}</div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)', marginTop: 2 }}>
                    {o.arrivalUrl ? 'The QR code your guests scan.' : 'Issue this validation to a parker.'}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#7F3F98', marginTop: 2 }}>{validationDisplayLabel(o, validationOnly)}</div>
                  {fundingLine && (
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                      <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--color-gray-800)', background: 'var(--color-gray-100)', borderRadius: 999, padding: '3px 8px' }}>{fundingLine.label}</span>
                      <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--color-gray-600)' }}>{fundingLine.detail}</span>
                    </div>
                  )}
                  {/* The bound's consumption, from the server. Reporting readers
                      only: a Validation-only user's response carries no figure. */}
                  {commercialLine && (
                    <div style={{ fontSize: 12, fontWeight: 700, marginTop: 4, color: o.commercial?.warning || o.commercial?.status === 'reached' ? '#92400e' : 'var(--color-gray-600)' }}>
                      {commercialLine}
                    </div>
                  )}
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-gray-700)', marginTop: 4 }}>
                    {o.activeHours ? `Available: ${formatValidationHours(o.activeHours)}` : formatValidationHours(o.activeHours)}
                    {o.activeHours && <span style={{ fontWeight: 600, color: 'var(--color-gray-600)' }}> · zone local time{lotZoneAbbrev(issueNow, o.timezone) ? ` (${lotZoneAbbrev(issueNow, o.timezone)})` : ''}</span>}
                  </div>
                  {estimate && (
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)', marginTop: 2 }}>{estimate}</div>
                  )}
                  {/* Exact, per offer — never a cross-offer total. Uncapped
                      offers say so rather than showing a blank where a number
                      belongs. */}
                  {o.inventory && (
                    <div style={{
                      fontSize: 12, fontWeight: 700, marginTop: 4,
                      color: isRunningLow(o) ? '#92400e' : 'var(--color-gray-600)',
                    }}>
                      {remainingOf(o) ?? '—'} of {o.inventory.cap} left {periodWord(o.inventory.reset)}
                    </div>
                  )}
                  {o.inventory && (
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-gray-600)', marginTop: 2 }}>
                      Allowance resets {inventoryResetLabel(o.inventory.reset)}.
                    </div>
                  )}
                  <div style={{ borderTop: '1px solid var(--color-gray-100)', marginTop: 10, paddingTop: 10 }}>
                    <OfferQrActions offer={o} copied={copiedCode === o.code} onCopied={() => setCopiedCode(o.code)} />
                  </div>
                </div>
                <button
                  disabled={exhausted}
                  onClick={() => openIssue(o)}
                  style={{
                    padding: '10px 18px', borderRadius: buttonRadius, cursor: exhausted ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                    fontWeight: 700, fontSize: 13, flex: '0 0 auto',
                    background: exhausted ? 'var(--color-gray-100)' : lookupOpen && activeCode === o.code ? 'rgba(127,63,152,0.10)' : '#7F3F98',
                    color: exhausted ? 'var(--color-gray-600)' : lookupOpen && activeCode === o.code ? '#7F3F98' : '#fff',
                    border: exhausted ? '1px solid var(--color-gray-200)' : 'none',
                  }}
                >
                  {exhausted ? usedUpLabel(o) : lookupOpen && activeCode === o.code ? 'Issuing below ↓' : 'Issue validation'}
                </button>
              </div>
              )
            })}
          </div>
        </div>
      )}

      {/* The UNSCOPED ask: for a tenant who wants more before any warning has
          fired and does not yet know which program will run out. Offered when
          any program can run out at all, by its inventory or its commercial
          bound (TEN-08: a spend-limited or prepaid program with no inventory
          used to have no way to ask). Its receipt is its own; a program's ask
          above never marks this one sent, or the other way round. */}
      {offers.some(canRunOut) && canManage && (
        <div style={{ border: '1px dashed var(--color-gray-300)', borderRadius: 10, padding: '11px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)', lineHeight: 1.5, flex: '1 1 240px' }}>
            {unscopedAsk === 'sent'
              ? 'Request sent — your Parkify contact will review the allowance.'
              : validationOnly
                ? 'Need more? Ask your Parkify contact to raise this offer’s allowance.'
                : 'Need more? Ask your Parkify contact to raise an offer’s allowance. Nothing is charged until they agree.'}
          </div>
          <button type="button" onClick={() => void sendRequest(null)} disabled={unscopedAsk !== 'idle'} style={{ ...ghostBtnStyle, padding: '8px 12px', fontSize: 12, opacity: unscopedAsk === 'idle' ? 1 : .65 }}>
            {unscopedAsk === 'sent' ? 'Requested' : unscopedAsk === 'sending' ? 'Sending…' : 'Request more validations'}
          </button>
          {askErrorOf(null) && <div style={{ width: '100%', color: '#b91c1c', fontSize: 12, fontWeight: 600 }}>{askErrorOf(null)}</div>}
        </div>
      )}

      {lookupOpen && (
        <div ref={lookupRef}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
            <button
              onClick={() => setLookupOpen(false)}
              style={{ background: 'none', border: 'none', color: 'var(--color-gray-600)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
            >
              Hide ✕
            </button>
          </div>
          {/* Remounts on offer switch (key) — Validate.tsx only resolves
              `initialCode` once, on mount (see its own load() effect), so
              picking a different offer card needs a fresh instance rather
              than a prop it won't re-read. */}
          <Validate key={activeCode} session={session} tenant={me.tenant} initialCode={activeCode} hideMonetaryValues={validationOnly} />
        </div>
      )}

      {issueModalOpen && (
        <div style={issueOverlay} onClick={() => setIssueModalOpen(false)}>
          <div style={issueModal} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="issue-validation-title">
            <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--color-gray-200)', display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <h2 id="issue-validation-title" style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>Issue validation</h2>
                <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--color-gray-600)', marginTop: 4 }}>{me.tenant.name}</div>
              </div>
              <button type="button" aria-label="Close" onClick={() => setIssueModalOpen(false)} style={{ background: 'none', border: 0, color: 'var(--color-gray-600)', fontSize: 22, cursor: 'pointer', alignSelf: 'flex-start' }}>×</button>
            </div>
            <div style={{ padding: '20px 24px' }}>
              {issueOffers.length === 0 ? (
                <div style={{ background: 'rgba(245,158,11,.10)', border: '1px solid rgba(245,158,11,.35)', borderRadius: 11, padding: '13px 15px', color: '#92400e' }}>
                  <div style={{ fontSize: 13, fontWeight: 800 }}>
                    {closedOffers.length === 0 ? 'Every validation is used up' : closedClock ? `Nothing runs at ${closedClock}` : 'Nothing runs right now'}
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.5, marginTop: 4 }}>
                    {closedOffers.length === 0 ? 'Nothing can be issued until an allowance resets or is raised.' : 'Nothing can be issued until a program opens.'}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 9 }}>
                    {issueList.map(o => (
                      <div key={o.code} style={{ fontSize: 11.5, fontWeight: 700 }}>
                        {o.name || o.code} · {!offerAvailableNow(o) ? formatValidationHours(o.activeHours) : usedUpLabel(o)}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                  {issueList.map(o => {
                    const available = offerIssuable(o)
                    const usedUp = isOfferUsedUp(o)
                    const selected = selectedIssueOffer?.code === o.code
                    return (
                    <button key={o.code} type="button" disabled={!available} onClick={available ? () => setIssueOffer(o) : undefined} style={{ width: '100%', textAlign: 'left', cursor: available ? 'pointer' : 'default', border: `1.5px solid ${selected ? '#7F3F98' : 'var(--color-gray-200)'}`, background: selected ? 'rgba(127,63,152,.07)' : '#fff', borderRadius: 11, padding: '11px 13px', fontFamily: 'inherit', opacity: available ? 1 : .6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 15, fontWeight: 800, color: selected ? '#5D2D70' : available ? 'var(--color-gray-800)' : 'var(--color-gray-600)' }}>{o.name || o.code}</span>
                        {!available && <span style={{ fontSize: 9.5, fontWeight: 800, color: 'var(--color-gray-600)', background: 'var(--color-gray-100)', padding: '2px 7px', borderRadius: 5, letterSpacing: '.07em' }}>{usedUp ? usedUpLabel(o).toUpperCase() : 'NOT AT THIS TIME'}</span>}
                      </div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)', marginTop: 2 }}>{validationDisplayLabel(o, validationOnly)} · {formatValidationHours(o.activeHours)}</div>
                    </button>
                    )
                  })}
                </div>
              )}
            </div>
            <div style={{ padding: '14px 24px 18px', borderTop: '1px solid var(--color-gray-200)', background: '#FCFBFD', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" onClick={() => setIssueModalOpen(false)} style={ghostBtnStyle}>Cancel</button>
              <button type="button" disabled={!selectedIssueOffer} onClick={() => { if (!selectedIssueOffer) return; setActiveCode(selectedIssueOffer.code); setLookupOpen(true); setIssueModalOpen(false) }} style={{ ...primaryBtnStyle, opacity: selectedIssueOffer ? 1 : .6 }}>Continue</button>
            </div>
          </div>
        </div>
      )}

      {canReport && (
        <div style={{ marginTop: 26 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 0 12px' }}>
            <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--color-gray-600)', letterSpacing: '.12em', textTransform: 'uppercase' }}>Reporting</span>
            <span style={{ flex: 1, height: 1, background: 'var(--color-gray-200)' }} />
          </div>
          <History session={session} hideMonetaryValues={validationOnly} />
        </div>
      )}

      {canManage && (
        <div style={{ marginTop: 26 }}>
          <div style={labelStyle}>Validation codes</div>
          <Dashboard session={session} me={me} hideMonetaryValues={validationOnly} canManageTenant={tenantCan(me, 'users.manage')} />
        </div>
      )}

      {/* Stations (paired tablets) live HERE, not under a Settings tab — which is
          where the demo puts them, in this same Validation area. A paired tablet
          is not an account setting; it is the thing that ISSUES validations, so it
          belongs beside the code a guest scans and the log of what was redeemed.
          Settings.tsx used to justify holding it by claiming the demo "never
          depicted" Tablets — it does, in `TValidation`, which takes
          `stations`/`setStations` as props. */}
      {canManage && (
        <div style={{ marginTop: 26 }}>
          <Stations session={session} />
        </div>
      )}
    </div>
  )
}
